import React, { useState } from "react";
import { adminBroadcast } from "../../lib/api";

/**
 * BroadcastSection — composes an admin-wide inbox message. Three scopes:
 *   global         — every non-suspended user
 *   league_members — every member of one specific league
 *   user_ids       — paste-in list, max 500
 *
 * Phase 54 power-tool. Lives next to LeaguesSection in the admin panel.
 */
export function BroadcastSection({ onToast }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState("global");
  const [scopeLeague, setScopeLeague] = useState("");
  const [scopeIds, setScopeIds] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canSend =
    title.trim().length >= 1 &&
    body.trim().length >= 1 &&
    (scope !== "league_members" || scopeLeague.trim()) &&
    (scope !== "user_ids"       || scopeIds.trim());

  const send = async () => {
    if (!canSend) return;
    const ids = scope === "user_ids"
      ? scopeIds.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean).slice(0, 500)
      : null;
    if (!window.confirm(
      `Send "${title}" to scope=${scope}? This is final once sent.`
    )) return;
    setBusy(true);
    try {
      const res = await adminBroadcast({
        title: title.trim(),
        body:  body.trim(),
        scope,
        scope_league_id: scope === "league_members" ? scopeLeague.trim() : undefined,
        scope_user_ids:  scope === "user_ids" ? ids : undefined,
        link_url:        linkUrl.trim() || undefined,
      });
      setLastResult(res);
      onToast?.(`📡 Broadcast sent to ${res.recipients_count} user(s).`);
      setTitle(""); setBody(""); setLinkUrl(""); setScopeIds("");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't send broadcast.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-broadcast-section" className="space-y-4">
      <div className="glass rounded-2xl p-4 border border-[#14b8a6]/20">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-3">
          <i className="fas fa-tower-broadcast mr-1.5" />NEW BROADCAST
        </p>
        <label className="block text-[10px] tracking-widest text-white/55 mb-1">TITLE</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Maintenance window 02:00 UTC"
          data-testid="admin-broadcast-title"
          className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm mb-3"
        />
        <label className="block text-[10px] tracking-widest text-white/55 mb-1">BODY</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          rows={4}
          placeholder="Hey operators — we're shipping a small fix in 30 min…"
          data-testid="admin-broadcast-body"
          className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm mb-3"
        />
        <label className="block text-[10px] tracking-widest text-white/55 mb-1">LINK URL (optional)</label>
        <input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          maxLength={400}
          placeholder="/leagues/your-league"
          data-testid="admin-broadcast-link"
          className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm mb-3"
        />

        <label className="block text-[10px] tracking-widest text-white/55 mb-1">SCOPE</label>
        <div className="flex gap-2 mb-3" data-testid="admin-broadcast-scope">
          {[
            { id: "global",         label: "GLOBAL"    },
            { id: "league_members", label: "LEAGUE"    },
            { id: "user_ids",       label: "USER IDS"  },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setScope(s.id)}
              data-testid={`admin-broadcast-scope-${s.id}`}
              className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg border ${
                scope === s.id
                  ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/55"
                  : "bg-black/40 text-white/65 border-white/15 hover:border-[#14b8a6]/40"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {scope === "league_members" && (
          <input
            value={scopeLeague}
            onChange={(e) => setScopeLeague(e.target.value)}
            placeholder="league_id (lg_…)"
            data-testid="admin-broadcast-scope-league-id"
            className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm mb-3"
          />
        )}
        {scope === "user_ids" && (
          <textarea
            value={scopeIds}
            onChange={(e) => setScopeIds(e.target.value)}
            rows={3}
            placeholder="Comma- or whitespace-separated user_ids (max 500)"
            data-testid="admin-broadcast-scope-user-ids"
            className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm mb-3 font-mono"
          />
        )}

        <button
          type="button"
          onClick={send}
          disabled={!canSend || busy}
          data-testid="admin-broadcast-send"
          className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-40"
        >
          <i className="fas fa-paper-plane mr-1" />
          {busy ? "SENDING…" : "SEND BROADCAST"}
        </button>
      </div>

      {lastResult && (
        <div
          data-testid="admin-broadcast-last-result"
          className="glass rounded-2xl p-3 border border-[#14b8a6]/30 text-[12px] text-white/80"
        >
          <p className="font-bold text-[#14b8a6] mb-1">Last broadcast</p>
          <p>id: <span className="font-mono">{lastResult.broadcast_id}</span></p>
          <p>recipients: <span className="font-mono">{lastResult.recipients_count}</span></p>
          <p>scope: <span className="font-mono">{lastResult.scope}</span></p>
        </div>
      )}
    </div>
  );
}

export default BroadcastSection;
